import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async"; // ✅ Shto për SEO tags
import { usePosts } from "../Hooks/usePosts";
import useToggle from "../Hooks/useToggle";
import Drawer from "../components/Mobile/Drawer";
import Footer from "../components/Partials/Footers/HomeOne/Footer";
import BackToTop from "../components/Helpers/BackToTop";
import dateFormat from "../lib/dateFormat";
import { navigationItems } from "../utils/utils";

const SITE_URL =
  "fastidious-lamington-55c5c5.netlify.app" ||
  "https://balotazh.com" ||
  "https://www.balotazh.com";

const buildCanonicalUrl = (post) => {
  const path = `/news/${post.id}`;
  try {
    return new URL(path, SITE_URL).toString();
  } catch {
    return `${SITE_URL.replace(/\/$/, "")}${path}`;
  }
};

const NewsDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: posts, isLoading, error } = usePosts();
  const [drawer, setDrawer] = useToggle(false);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [isSharing, setIsSharing] = useState(false);

  const post = posts?.find((p) => p.id === id);

  const relatedPosts =
    posts
      ?.filter(
        (p) =>
          p.id !== id &&
          p.categories?.some((cat) => post?.categories?.includes(cat))
      )
      .slice(0, 4) || [];

  const latestPosts = posts?.filter((p) => p.id !== id).slice(0, 5) || [];

  const handleGoBack = () => navigate(-1);
  const handleImageLoad = () => setIsImageLoading(false);
  const handleImageError = () => setIsImageLoading(false);

  const handleShare = (e, platform) => {
    e.preventDefault();
    e.stopPropagation();
    if (!post) return;

    setIsSharing(true);
    const shareUrl = buildCanonicalUrl(post);
    const title = post.title || document.title;

    const openPopup = (url) =>
      window.open(url, "_blank", "noopener,noreferrer");

    const shareLinks = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        shareUrl
      )}&quote=${encodeURIComponent(title)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(
        shareUrl
      )}&text=${encodeURIComponent(title)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        shareUrl
      )}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(`${title} ${shareUrl}`)}`,
    };

    if (shareLinks[platform]) {
      openPopup(shareLinks[platform]);
    } else if (platform === "native" && navigator.share) {
      navigator.share({ title, text: title, url: shareUrl }).catch(() => {});
    } else if (platform === "copy") {
      navigator.clipboard?.writeText(shareUrl).catch(() => {});
    }

    setIsSharing(false);
  };

  const calculateReadingTime = (content) => {
    if (!content) return 5;
    const wordsPerMinute = 200;
    const wordCount = content.split(" ").length;
    return Math.ceil(wordCount / wordsPerMinute);
  };

  useEffect(() => window.scrollTo(0, 0), [id]);

  useEffect(() => {
    if (post?.title) document.title = post.title;
    return () => (document.title = "Balotazhi");
  }, [post]);

  if (error)
    return (
      <div className="error-container">
        <div className="container">
          <div className="error-content">
            <div className="error-icon">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <h2>Gabim në ngarkim</h2>
            <p>
              Ndodhi një gabim gjatë ngarkimit të artikullit. Ju lutem provoni
              përsëri.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="btn btn-primary"
            >
              <i className="fas fa-redo"></i> Provoni përsëri
            </button>
          </div>
        </div>
      </div>
    );

  if (isLoading)
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
          <p>Duke ngarkuar artikullin...</p>
        </div>
      </div>
    );

  if (!post)
    return (
      <div className="not-found-container">
        <div className="container">
          <div className="not-found-content">
            <div className="not-found-icon">
              <i className="fas fa-search"></i>
            </div>
            <h2>Artikulli nuk u gjet</h2>
            <p>Artikulli që po kërkoni nuk ekziston ose është zhvendosur.</p>
            <div className="not-found-actions">
              <button onClick={handleGoBack} className="btn btn-secondary">
                <i className="fas fa-arrow-left"></i> Kthehu
              </button>
              <Link to="/" className="btn btn-primary">
                <i className="fas fa-home"></i> Faqja kryesore
              </Link>
            </div>
          </div>
        </div>
      </div>
    );

  const readingTime = calculateReadingTime(post.content || post.excerpt);
  const articleUrl = buildCanonicalUrl(post);
  const imageUrl = post.image_url || `${SITE_URL}/default.jpg`;
  const description = post.excerpt || post.content?.slice(0, 160) || "";

  return (
    <>
      {/* ✅ META TAGS për Facebook, Twitter, dhe SEO */}
      <Helmet>
        <title>{post.title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={articleUrl} />

        {/* ✅ Open Graph Tags */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:url" content={articleUrl} />
        <meta property="og:site_name" content="Balotazh News" />

        {/* ✅ Twitter Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={imageUrl} />
      </Helmet>

      <Drawer drawer={drawer} action={setDrawer.toggle} />

      <header className="article-header">
        <div className="container">
          <nav className="article-navigation" aria-label="Breadcrumb">
            <button
              onClick={handleGoBack}
              className="back-button"
              aria-label="Kthehu në faqen e mëparshme"
            >
              <i className="fas fa-arrow-left"></i>
              <span>Kthehu</span>
            </button>

            <ol className="article-breadcrumb">
              <li>
                <Link to="/">Lajme</Link>
              </li>
              {post.categories?.[0] && (
                <>
                  <li className="separator">/</li>
                  <li>
                    <Link to={`/category/${post.categories[0].toLowerCase()}`}>
                      {post.categories[0]}
                    </Link>
                  </li>
                </>
              )}
            </ol>
          </nav>
        </div>
      </header>

      <main className="article-content">
        <div className="container">
          <div className="row">
            <div className="col-lg-8">
              <article
                className="main-article"
                itemScope
                itemType="http://schema.org/NewsArticle"
              >
                <header className="article-header-content">
                  <div className="article-categories">
                    {post.categories?.map((category, index) => (
                      <Link
                        key={index}
                        to={`/category/${category.toLowerCase()}`}
                        className="article-category"
                      >
                        {category}
                      </Link>
                    ))}
                  </div>

                  <h1 className="article-title" itemProp="headline">
                    {post.title}
                  </h1>

                  <div className="article-meta">
                    <div className="article-author" itemProp="author">
                      <i className="fas fa-user" aria-hidden="true"></i>
                      <span>{post.author || "Redaksia"}</span>
                    </div>
                    <div className="article-date">
                      <i className="fas fa-calendar" aria-hidden="true"></i>
                      <time dateTime={post.created_at} itemProp="datePublished">
                        {dateFormat(post.created_at)}
                      </time>
                    </div>
                    <div className="article-reading-time">
                      <i className="fas fa-clock" aria-hidden="true"></i>
                      <span>{readingTime} min lexim</span>
                    </div>
                  </div>
                </header>

                <div className="article-featured-image">
                  {isImageLoading && (
                    <div className="image-skeleton">
                      <div className="skeleton-loader"></div>
                    </div>
                  )}
                  <img
                    src={imageUrl}
                    alt={post.title}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    className={isImageLoading ? "hidden" : ""}
                    itemProp="image"
                  />
                </div>

                <div className="article-body">
                  <div className="article-excerpt">
                    <p className="lead" itemProp="description">
                      {post.excerpt}
                    </p>
                  </div>

                  <div className="article-content-text" itemProp="articleBody">
                    {post.content ? (
                      <div dangerouslySetInnerHTML={{ __html: post.content }} />
                    ) : (
                      <p>{post.excerpt}</p>
                    )}
                  </div>

                  {post.tags && post.tags.length > 0 && (
                    <div className="article-tags">
                      <h4>Etiketat:</h4>
                      <div className="tags-list">
                        {post.tags.map((tag, index) => (
                          <span key={index} className="tag">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="article-share">
                    <h4>Ndaj këtë artikull:</h4>
                    <div className="share-buttons">
                      <button
                        className="share-btn facebook"
                        onClick={(e) => handleShare(e, "facebook")}
                      >
                        <i className="fab fa-facebook-f"></i>
                        <span>Facebook</span>
                      </button>
                      <button
                        className="share-btn twitter"
                        onClick={(e) => handleShare(e, "twitter")}
                      >
                        <i className="fab fa-twitter"></i>
                        <span>Twitter</span>
                      </button>
                      <button
                        className="share-btn linkedin"
                        onClick={(e) => handleShare(e, "linkedin")}
                      >
                        <i className="fab fa-linkedin-in"></i>
                        <span>LinkedIn</span>
                      </button>
                      <button
                        className="share-btn whatsapp"
                        onClick={(e) => handleShare(e, "whatsapp")}
                      >
                        <i className="fab fa-whatsapp"></i>
                        <span>WhatsApp</span>
                      </button>
                      <button
                        className="share-btn copy"
                        onClick={(e) => handleShare(e, "copy")}
                      >
                        <i className="fas fa-link"></i>
                        <span>Kopjo linkun</span>
                      </button>
                    </div>
                  </div>
                </div>
              </article>

              {relatedPosts.length > 0 && (
                <section className="related-articles">
                  <h3 className="section-title">Artikuj të ngjashëm</h3>
                  <div className="related-articles-grid">
                    <div className="row g-4">
                      {relatedPosts.map((relatedPost) => (
                        <div
                          key={relatedPost.id}
                          className="col-lg-6 col-md-6 col-sm-12"
                        >
                          <article className="related-article">
                            <Link to={`/news/${relatedPost.id}`}>
                              <div className="related-article-image">
                                <img
                                  src={relatedPost.image_url}
                                  alt={relatedPost.title}
                                  loading="lazy"
                                />
                              </div>
                              <div className="related-article-content">
                                <span className="related-article-category">
                                  {relatedPost.categories?.[0]}
                                </span>
                                <h4 className="related-article-title">
                                  {relatedPost.title}
                                </h4>
                                <div className="related-article-meta">
                                  <time className="related-article-date">
                                    {dateFormat(relatedPost.created_at)}
                                  </time>
                                </div>
                              </div>
                            </Link>
                          </article>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              )}
            </div>

            <aside className="col-lg-4">
              <div className="article-sidebar">
                <div className="sidebar-section">
                  <h3 className="sidebar-title">Lajmet e fundit</h3>
                  <div className="sidebar-posts">
                    {latestPosts.map((latestPost) => (
                      <article key={latestPost.id} className="sidebar-post">
                        <Link to={`/news/${latestPost.id}`}>
                          <div className="sidebar-post-image">
                            <img
                              src={latestPost.image_url}
                              alt={latestPost.title}
                              loading="lazy"
                            />
                          </div>
                          <div className="sidebar-post-content">
                            <h4 className="sidebar-post-title">
                              {latestPost.title}
                            </h4>
                            <time className="sidebar-post-date">
                              {dateFormat(latestPost.created_at)}
                            </time>
                          </div>
                        </Link>
                      </article>
                    ))}
                  </div>
                </div>

                <div className="sidebar-section">
                  <h3 className="sidebar-title">Kategoritë popullore</h3>
                  <div className="popular-categories">
                    {navigationItems
                      .filter((item) => item.category)
                      .map((item) => (
                        <Link
                          key={item.id}
                          to={item.path}
                          className="category-link"
                        >
                          {item.name}
                        </Link>
                      ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
      <BackToTop />
    </>
  );
};

export default NewsDetail;
